import React, { useCallback, useEffect } from 'react'
import DigitalAssetDetailsForm from './DigitalAssetDetailsForm/DigitalAssetDetailsForm'
import { AntDHeader } from 'views/ui/Header/Header'
import { useDigitalAsset } from 'modules/domain/digitalAsset/hooks'
import { useParams } from 'react-router-dom'
import { Progress } from 'modules/types'
import { useSelector } from 'react-redux'
import DigitalAssetSelectors from 'modules/domain/digitalAsset/selectors'
import DigitalAssetActions from 'modules/domain/digitalAsset/duck'
import Routes from './routes'
import { useTranslation } from 'react-i18next'
import { useAction, useHelmet, useHistoryPush } from '@agro-club/frontend-shared'
import { Button, Flex, Modal, Typography } from 'antd'
import { DeleteOutlined, DownloadOutlined, WarningOutlined } from '@ant-design/icons'
import ItemLoadingLayout from '../../layouts/ItemLoadingLayout/ItemLoadingLayout'
import Item404 from '../../layouts/Item404/Item404'
import ItemGenericError from '../../layouts/ItemGenericError/ItemGenericError'
import CSS from 'csstype'
import { DigitalAsset } from '../../../modules/domain/digitalAsset/types'
import AuthSelectors from 'modules/domain/auth/selectors'
import { UploadCompanyType } from 'types/entities'

const { Title } = Typography

const headerStyle: CSS.Properties = {
  paddingLeft: '1em',
  paddingRight: '1em',
}
const titleStyle: CSS.Properties = {
  fontWeight: 'bold',
  paddingLeft: '1em',
  marginBlock: 0,
}

const headerButtonStyle: CSS.Properties = {
  paddingRight: '1.5em',
}

const secondaryHeaderStyle: CSS.Properties = {
  paddingBottom: '1em',
}

const DigitalAssetEdit: React.FC = () => {
  const params = useParams<{ id: string }>()
  const push = useHistoryPush()
  const downloadDigitalAssetFileAction = useAction(DigitalAssetActions.downloadFileRequested)
  const softDeleteAction = useAction(DigitalAssetActions.softDeleteRequested)
  const page = useSelector(DigitalAssetSelectors.page)
  const { t } = useTranslation('digitalAsset')
  const [modal, contextHolder] = Modal.useModal()
  const handleDownloadFile = useCallback(async (id: string, documentName?: string) => {
    downloadDigitalAssetFileAction([id, documentName])
  }, [])

  const [fetchProgress, digitalAsset] = useDigitalAsset(params.id)

  const meta = useSelector((state) => DigitalAssetSelectors.meta(state, params.id))

  const goBack = useCallback(() => push({ route: Routes.List, query: { page } }), [page, push])
  useEffect(() => {
    if (meta.updateProgress === Progress.SUCCESS) {
      goBack()
    }
  }, [goBack, meta.updateProgress])

  const isAdminOrProducer = useSelector(AuthSelectors.isAdminOrProducer)
  const isAdmin = useSelector(AuthSelectors.isAdmin)
  const userCompanyType = isAdminOrProducer ? UploadCompanyType.Producer : UploadCompanyType.Distributor
  const canEditOrDelete = isAdmin || (digitalAsset && userCompanyType === digitalAsset.uploaded_by_company_type)

  useEffect(() => {
    if (digitalAsset && !canEditOrDelete) {
      goBack()
    }
  }, [digitalAsset, canEditOrDelete, goBack])

  const handleSoftDelete = (assets: DigitalAsset[]) => {
    const confirmContent = assets.map((asset) => {
      return <p key={asset.id}>{asset.document_name || asset.link_url}</p>
    })
    modal.confirm({
      title: t('messages.deleteMessage'),
      content: confirmContent,
      icon: <WarningOutlined />,
      cancelText: t('button.noConfirm'),
      okText: t('button.yesConfirm'),
      onOk: () => {
        const ids = assets.map((asset) => asset.id)
        softDeleteAction(ids)
        goBack()
      },
    })
  }

  useHelmet({ title: t('editDigitalAssetMetaTitle') })

  const loading = () => <ItemLoadingLayout id={params.id} onBack={goBack} />
  const error404 = () => <Item404 id={params.id} onBack={goBack} title={t('errors.notFoundTitle')} />
  const errorUnknown = () => <ItemGenericError id={params.id} onBack={goBack} title={t('errors.unknownErrorTitle')} />

  if (fetchProgress === Progress.ERROR) {
    if (meta.fetchError === 'not_found') {
      return error404()
    }
    return errorUnknown()
  }

  if (fetchProgress === Progress.WORK || !digitalAsset) {
    return loading()
  }

  return (
    <>
      {contextHolder}
      <AntDHeader onClickBack={goBack} styles={headerStyle} />
      <Flex align="center" justify={'space-between'} style={secondaryHeaderStyle}>
        <Title level={2} style={titleStyle}>
          {digitalAsset.document_name || digitalAsset.link_url || ''}
        </Title>
        <Flex style={headerButtonStyle} gap="middle" justify="flex-end">
          <Button
            size="large"
            type="primary"
            danger
            iconPosition="start"
            icon={<DeleteOutlined />}
            onClick={() => handleSoftDelete([digitalAsset])}
          >
            {t('button.delete')}
          </Button>
          <Button
            size="large"
            type="primary"
            icon={<DownloadOutlined />}
            iconPosition="start"
            onClick={() => handleDownloadFile(params.id, digitalAsset.document_name)}
          >
            {t('button.download')}
          </Button>
        </Flex>
      </Flex>
      <DigitalAssetDetailsForm digitalAsset={digitalAsset} onCancel={goBack} />
    </>
  )
}

export default DigitalAssetEdit
